import { IExportRowData } from "api/interfaces";
import ContractModel from "api/models/ContractModel";
import ExcelService from "api/services/ExcelService";
import ExportXLSXService from "api/services/ExportXLSXService";
import FileService from "api/services/FileService";
import MeasurementService from "api/services/MeasurementService";
import {
  IAddAndCancelMovimentRow,
  IExportImage,
  IOtherChangeMovimentRow,
} from "components/interfaces";
import { lang } from "i18nLang";
import CompanyStore from "./CompanyStore";
import ContractStore from "./Contract/ContractStore";
import {
  additivesColumns,
  cancellationsColumns,
  measurementsColumns,
  otherChangesColumns,
} from "./data/exportColumns";
import MeasurementListStore from "./Measurement/MeasurementListStore";

class Store {
  public async exportMeasurementsList(
    rows: IExportRowData[],
    footerData: IExportRowData
  ) {
    const contract = ContractStore.getCurrentContract();
    if (!contract) return;

    const company = CompanyStore.currentCompany;
    const companyImage = await this.convertCompanyImageToBase64(company?.image);
    const title = lang("exportData.measurements.title");

    await this.processMeasurementsData(rows);
    await this.getMeasurementsClientsNames(contract, rows);

    const workbook = ExportXLSXService.createWorkbook();
    ExportXLSXService.createWorsheet(
      workbook,
      contract,
      title,
      measurementsColumns,
      rows,
      companyImage,
      footerData
    );
    await workbook.saveAs(contract.name + "_" + title);
  }

  private processMeasurementsData(rows: IExportRowData[]) {
    for (const row of rows) {
      const billingOrder = row.billingOrder;
      row.period = `${row.initialDate.toLocaleDateString(
        "pt-br"
      )} a ${row.finalDate.toLocaleDateString("pt-br")}`;
      row.orderCode =
        billingOrder.orderCode !== "0" ? billingOrder.orderCode : "";
      row.invoiceNumber = billingOrder.invoiceNumber;
      row.invoiceSerial = billingOrder.invoiceSerial;
      row.invoiceDate = billingOrder.invoiceDate;
    }
  }

  public async exportMovimentsData(
    additivesMoviments: IAddAndCancelMovimentRow[],
    cancelationsMoviments: IAddAndCancelMovimentRow[],
    otherChangesMoviments: IOtherChangeMovimentRow[]
  ) {
    const contract = ContractStore.getCurrentContract();
    if (!contract) return;

    const company = CompanyStore.currentCompany;
    const companyImage = await this.convertCompanyImageToBase64(company?.image);
    const title = lang("exportData.addsAndCancels");

    const workbook = ExportXLSXService.createWorkbook();
    this.createAdditivesMovimentsWorksheet(
      workbook,
      contract,
      additivesMoviments,
      companyImage
    );
    this.createCancelationsMovimentsWorksheet(
      workbook,
      contract,
      cancelationsMoviments,
      companyImage
    );
    this.createOtherChangesMovimentsWorksheet(
      workbook,
      contract,
      otherChangesMoviments,
      companyImage
    );

    await workbook.saveAs(contract.name + "_" + title);
  }

  public async createAdditivesMovimentsWorksheet(
    workbook: ExcelService,
    contract: ContractModel,
    additivesMoviments: IAddAndCancelMovimentRow[],
    companyImage?: IExportImage
  ) {
    const title = lang("indicatorsPage.addsAndCancels.adds");
    ExportXLSXService.createWorsheet(
      workbook,
      contract,
      title,
      additivesColumns,
      additivesMoviments,
      companyImage
    );
  }

  public async createCancelationsMovimentsWorksheet(
    workbook: ExcelService,
    contract: ContractModel,
    cancelationsMoviments: IAddAndCancelMovimentRow[],
    companyImage?: IExportImage
  ) {
    const title = lang("indicatorsPage.addsAndCancels.cancels");
    ExportXLSXService.createWorsheet(
      workbook,
      contract,
      title,
      cancellationsColumns,
      cancelationsMoviments,
      companyImage
    );
  }

  public async createOtherChangesMovimentsWorksheet(
    workbook: ExcelService,
    contract: ContractModel,
    otherChangesMoviments: IOtherChangeMovimentRow[],
    companyImage?: IExportImage
  ) {
    const title = lang("indicatorsPage.addsAndCancels.otherChanges");
    ExportXLSXService.createWorsheet(
      workbook,
      contract,
      title,
      otherChangesColumns,
      otherChangesMoviments,
      companyImage
    );
  }

  public async convertCompanyImageToBase64(
    companyImageUrl: string = ""
  ): Promise<IExportImage | undefined> {
    if (!companyImageUrl) return;

    const imageFile = await FileService.downloadFrom(companyImageUrl);
    if (!imageFile) return;

    const imageBase64 = await FileService.toBase64(imageFile);
    if (!imageBase64) return;

    const sizes = await FileService.getImageHeightAndWidth(
      companyImageUrl
    ).catch(() => undefined);
    const height = 80;
    const width = sizes ? (sizes.width / sizes.height) * height : 400;

    return {
      base64: imageBase64 || "",
      extension: "png",
      width,
      height,
    };
  }

  private async getMeasurementsClientsNames(
    contract: ContractModel,
    rows: IExportRowData[]
  ) {
    const promises = MeasurementListStore.getMeasurements().map((m) =>
      MeasurementService.getMeasurementBillingData(m)
    );
    const billingDatas = await Promise.all(promises);

    rows.forEach((row) => {
      const billing = billingDatas.find(
        (data) => data?.measurementUid === row.uid
      );
      row.clientName = billing?.client.name || contract.development?.customer;
    });
  }
}

const ExportStore = new Store();
export default ExportStore;
